/*
  LUMINANTE — Ma sélection.

  Liste de produits favoris, stockée dans le navigateur du visiteur.
  Aucun compte requis, aucune application : la contrepartie assumée est
  que la sélection ne suit pas d'un appareil à l'autre.

  Le stockage ne garde que des handles de produits. Les cartes sont
  rendues par Liquid via le gabarit `product.carte`, ce qui évite de
  dupliquer en JavaScript le balisage et les styles des cartes.

  Deux précautions qui évitent les bugs classiques :
    · délégation d'événement sur le document, pour que les cœurs des
      cartes re-rendues par Horizon (filtres, panier) restent actifs ;
    · resynchronisation à chaque mutation du DOM, pour que les cartes
      arrivées après coup affichent le bon état.
*/

(() => {
  const CLE = 'luminante:selection';
  const EVENEMENT = 'luminante:selection-modifiee';

  /** @returns {string[]} */
  function lire() {
    try {
      const brut = localStorage.getItem(CLE);
      const liste = brut ? JSON.parse(brut) : [];
      return Array.isArray(liste) ? liste.filter((h) => typeof h === 'string') : [];
    } catch {
      // Navigation privée ou stockage plein : la sélection est vide,
      // le site reste utilisable.
      return [];
    }
  }

  function ecrire(liste) {
    try {
      localStorage.setItem(CLE, JSON.stringify(liste));
    } catch {
      /* Rien à faire : on ne bloque jamais la navigation pour ça. */
    }
    document.dispatchEvent(new CustomEvent(EVENEMENT, { detail: { liste } }));
  }

  function contient(handle) {
    return lire().includes(handle);
  }

  function basculer(handle) {
    const liste = lire();
    const i = liste.indexOf(handle);
    if (i === -1) liste.push(handle);
    else liste.splice(i, 1);
    ecrire(liste);
    return i === -1;
  }

  /* --- Affichage --- */

  function synchroniserCoeurs() {
    const liste = lire();
    document.querySelectorAll('[data-lum-coeur]').forEach((bouton) => {
      const actif = liste.includes(bouton.dataset.lumCoeur);
      bouton.classList.toggle('lum-coeur--actif', actif);
      bouton.setAttribute('aria-pressed', actif ? 'true' : 'false');
    });
  }

  function synchroniserCompteur() {
    const n = lire().length;
    document.querySelectorAll('[data-lum-selection-compteur]').forEach((el) => {
      el.textContent = String(n);
    });
    // C'est la pastille entière qui se masque, pas seulement le
    // chiffre : sinon le rond resterait visible et vide.
    document.querySelectorAll('[data-lum-selection-bulle]').forEach((el) => {
      el.hidden = n === 0;
    });
  }

  function synchroniser() {
    synchroniserCoeurs();
    synchroniserCompteur();
  }

  /* --- Page « Ma sélection » --- */

  async function rendrePage() {
    const zone = document.querySelector('[data-lum-selection-liste]');
    if (!zone) return;

    const vide = document.querySelector('[data-lum-selection-vide]');
    const liste = lire();

    if (liste.length === 0) {
      zone.innerHTML = '';
      zone.hidden = true;
      if (vide) vide.hidden = false;
      return;
    }

    zone.hidden = false;
    if (vide) vide.hidden = true;

    // Un handle disparu (produit retiré du catalogue) est nettoyé de la
    // sélection plutôt que de laisser une case vide indéfiniment.
    const valides = [];
    const morceaux = await Promise.all(
      liste.map(async (handle) => {
        try {
          const reponse = await fetch(`/products/${encodeURIComponent(handle)}?view=carte`);
          if (!reponse.ok) return null;
          const html = (await reponse.text()).trim();
          if (!html) return null;
          valides.push(handle);
          return `<li class="lum-selection__item">${html}</li>`;
        } catch {
          return null;
        }
      })
    );

    zone.innerHTML = morceaux.filter(Boolean).join('');

    if (valides.length !== liste.length) ecrire(valides);
    synchroniserCoeurs();
  }

  /* --- Branchements --- */

  document.addEventListener('click', (e) => {
    const bouton = e.target.closest('[data-lum-coeur]');
    if (!bouton) return;
    e.preventDefault();
    e.stopPropagation();

    const handle = bouton.dataset.lumCoeur;
    basculer(handle);
    synchroniser();

    // Sur la page sélection, retirer un produit doit le faire
    // disparaître tout de suite.
    const item = bouton.closest('.lum-selection__item');
    if (item && !contient(handle)) {
      item.remove();
      const zone = document.querySelector('[data-lum-selection-liste]');
      if (zone && zone.children.length === 0) rendrePage();
    }
  });

  document.addEventListener(EVENEMENT, synchroniserCompteur);

  // Une autre onglet a modifié la sélection.
  window.addEventListener('storage', (e) => {
    if (e.key === CLE) {
      synchroniser();
      rendrePage();
    }
  });

  function demarrer() {
    synchroniser();
    rendrePage();

    // Les cartes peuvent arriver après coup (filtres de collection,
    // re-rendu du panier) : on resynchronise sans tout recalculer.
    let attente;
    new MutationObserver(() => {
      clearTimeout(attente);
      attente = setTimeout(synchroniser, 80);
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', demarrer);
  } else {
    demarrer();
  }
})();
